import { c as _c } from "react/compiler-runtime";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

// Поля ввода веба покупателя по макету (компонент `text-field` и поиск в шапке).
//
//   filled (по умолчанию) — заливка #1F1F1F 3 %, без рамки, r20, h40,
//          pad 10/14, Manrope 14/18 w500 #1F1F1F, плейсхолдер #1F1F1F 48 %
//          (оформление, профиль, фильтр цены, отзыв);
//   filled + size="lg" — h48, pad 12/16, 16/21 (поле сообщения/комментария);
//   square — то же, но r10 (промокод в корзине);
//   search — белое, обводка #EFEFEF, r100, h40, pad 6/10, 13/17 w500, иконка 16
//          (поиск в шапке).
// Фокус у всех — чёрная обводка 1px #1F1F1F (A-03, A-06), не оранжевая и без
// ореола. Ошибка — обводка и подпись #E1574F (aria-invalid или проп error).
// Рамка рисуется на обёртке (`data-slot="text-field"`), чтобы иконки слева и
// справа были внутри поля.
const fieldVariants = cva("group/field flex w-full min-w-0 items-center border border-transparent transition-[border-color,background-color] focus-within:border-ds-focus has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60 data-[invalid=true]:border-ds-error data-[invalid=true]:focus-within:border-ds-error", {
  variants: {
    variant: {
      filled: "rounded-[20px] bg-ds-field",
      square: "rounded-[10px] bg-ds-field",
      search: "rounded-full border-ds-line bg-white"
    },
    size: {
      md: "h-10 gap-2 px-[14px] text-[14px] leading-[18px]",
      lg: "h-12 gap-2 px-4 text-[16px] leading-[21px]"
    }
  },
  compoundVariants: [{
    variant: "search",
    className: "gap-2 px-2.5 text-[13px] leading-[17px] tracking-[-0.39px]"
  }],
  defaultVariants: {
    variant: "filled",
    size: "md"
  }
});
type FieldVariantProps = VariantProps<typeof fieldVariants>;
type InputProps = Omit<React.ComponentProps<"input">, "size"> & FieldVariantProps & {
  /** Иконка/элемент слева внутри поля (поиск — 16px #1F1F1F 64 %). */
  start?: React.ReactNode;
  /** Иконка/кнопка справа внутри поля (очистить, «₽», глаз). */
  end?: React.ReactNode;
  /** Классы обёртки-поля (рамка, ширина, отступы). `className` — у самого input. */
  fieldClassName?: string;
  /** Состояние ошибки: красная обводка (то же даёт aria-invalid). */
  invalid?: boolean;
};

/** Поле ввода без подписи: обёртка с рамкой + input. Ref ведёт на input. */
function Input(t0) {
  const $ = _c(30);
  let className;
  let end;
  let fieldClassName;
  let invalid;
  let props;
  let size;
  let start;
  let variant;
  if ($[0] !== t0) {
    ({
      variant,
      size,
      start,
      end,
      fieldClassName,
      className,
      invalid,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = end;
    $[3] = fieldClassName;
    $[4] = invalid;
    $[5] = props;
    $[6] = size;
    $[7] = start;
    $[8] = variant;
  } else {
    className = $[1];
    end = $[2];
    fieldClassName = $[3];
    invalid = $[4];
    props = $[5];
    size = $[6];
    start = $[7];
    variant = $[8];
  }
  const isInvalid = invalid || props["aria-invalid"] === true || props["aria-invalid"] === "true";
  const t1 = isInvalid || undefined;
  let t2;
  if ($[9] !== fieldClassName || $[10] !== size || $[11] !== variant) {
    t2 = cn(fieldVariants({
      variant,
      size
    }), fieldClassName);
    $[9] = fieldClassName;
    $[10] = size;
    $[11] = variant;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== start) {
    t3 = start != null && <span className="flex shrink-0 items-center text-ds-primary/64 [&_svg:not([class*='size-'])]:size-4">{start}</span>;
    $[13] = start;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const t4 = isInvalid || undefined;
  const t5 = variant === "search" && "placeholder:text-ds-primary/64";
  let t6;
  if ($[15] !== className || $[16] !== t5) {
    t6 = cn("h-full min-w-0 flex-1 bg-transparent font-medium text-ds-primary outline-none placeholder:text-ds-placeholder disabled:cursor-not-allowed [&::-webkit-search-cancel-button]:appearance-none", t5, className);
    $[15] = className;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== props || $[19] !== t4 || $[20] !== t6) {
    t7 = <input data-slot="ds-input" aria-invalid={t4} className={t6} {...props} />;
    $[18] = props;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== end) {
    t8 = end != null && <span className="flex shrink-0 items-center text-ds-placeholder [&_svg:not([class*='size-'])]:size-4">{end}</span>;
    $[22] = end;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t1 || $[25] !== t2 || $[26] !== t3 || $[27] !== t7 || $[28] !== t8) {
    t9 = <div data-slot="text-field" data-invalid={t1} className={t2}>{t3}{t7}{t8}</div>;
    $[24] = t1;
    $[25] = t2;
    $[26] = t3;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
}
type TextFieldProps = InputProps & {
  /** Подпись над полем: Manrope 12/16 (md) или 14/18 (lg) w500 #171A24 64 %. */
  label?: React.ReactNode;
  /** Подсказка под полем (#000 50 %). */
  hint?: React.ReactNode;
  /** Текст ошибки под полем (#E1574F); включает красную обводку. */
  error?: React.ReactNode;
  /** Классы внешнего контейнера (подпись + поле + подсказка). */
  containerClassName?: string;
};

/** Поле с подписью, подсказкой и ошибкой — как `text-field` в макете (label gap 6). */
function TextField(t0) {
  const $ = _c(31);
  let containerClassName;
  let error;
  let hint;
  let id;
  let invalid;
  let label;
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      label,
      hint,
      error,
      containerClassName,
      id,
      invalid,
      size,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = containerClassName;
    $[2] = error;
    $[3] = hint;
    $[4] = id;
    $[5] = invalid;
    $[6] = label;
    $[7] = props;
    $[8] = size;
  } else {
    containerClassName = $[1];
    error = $[2];
    hint = $[3];
    id = $[4];
    invalid = $[5];
    label = $[6];
    props = $[7];
    size = $[8];
  }
  const autoId = React.useId();
  const inputId = id ?? autoId;
  const hasError = error != null && error !== false && error !== "";
  const describedBy = hasError ? `${inputId}-error` : hint != null ? `${inputId}-hint` : undefined;
  let t1;
  if ($[9] !== containerClassName) {
    t1 = cn("flex w-full flex-col gap-1.5", containerClassName);
    $[9] = containerClassName;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== inputId || $[12] !== label || $[13] !== size) {
    t2 = label != null && <label htmlFor={inputId} className={cn("font-medium text-ds-label", size === "lg" ? "text-[14px] leading-[18px]" : "text-[12px] leading-[16px]")}>{label}</label>;
    $[11] = inputId;
    $[12] = label;
    $[13] = size;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const t3 = invalid || hasError;
  let t4;
  if ($[15] !== describedBy || $[16] !== inputId || $[17] !== props || $[18] !== size || $[19] !== t3) {
    t4 = <Input id={inputId} size={size} invalid={t3} aria-describedby={describedBy} {...props} />;
    $[15] = describedBy;
    $[16] = inputId;
    $[17] = props;
    $[18] = size;
    $[19] = t3;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== error || $[22] !== hasError || $[23] !== hint || $[24] !== inputId) {
    t5 = hasError ? <p id={`${inputId}-error`} className="text-[12px] leading-[16px] text-ds-error">{error}</p> : hint != null ? <p id={`${inputId}-hint`} className="text-[12px] leading-[16px] text-ds-secondary">{hint}</p> : null;
    $[21] = error;
    $[22] = hasError;
    $[23] = hint;
    $[24] = inputId;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== t1 || $[27] !== t2 || $[28] !== t4 || $[29] !== t5) {
    t6 = <div data-slot="text-field-container" className={t1}>{t2}{t4}{t5}</div>;
    $[26] = t1;
    $[27] = t2;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  return t6;
}
export { fieldVariants, Input, TextField };
export type { InputProps, TextFieldProps };